import { type Auth as FirebaseAuth, getIdToken } from 'firebase/auth';

import { getAdHubClientConfig } from '../config';
import { getAuth } from './Firebase';

async function performAppRequest(url: URL, bodyObject: any): Promise<any> {
    const body = JSON.stringify(bodyObject);

    // Old code used to catch an error, then return { error } - that is, a
    // plain object with an error key set to the caught error object.
    // I suppose to mimic an error response from the server. I don't think
    // there's much code that responds to that specific code path, so I'm not
    // preserving that functionality, but I want to note it was here.

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
    });

    if (response.status === 204) {
        try {
            // check to see if there was a body anyway; shouldn't be
            const errantBody = await response.arrayBuffer();
            if (errantBody.byteLength > 0) {
                console.warn(
                    `[API] Got HTTP 204 with body; ignoring. url=%O`,
                    url
                );
            }
        } catch (receiveError) {
            console.warn(
                `[API] Got HTTP 204 with body that caused an error; ignoring. url=%O error=%O`,
                url,
                receiveError
            );
        }
        // I'm pretty sure we don't even have 204 responses, but just in case,
        // turn them into empty objects.
        return {};
    }

    const isHttpError = response.status >= 400;
    const responseBodyText = await response.text();
    let responseBody: any;
    try {
        responseBody = JSON.parse(responseBodyText);
    } catch (jsonParseError) {
        if (isHttpError) {
            // Be a little lenient in this case; move the text body into an 'error' property
            console.warn(
                `[API] Response with status ${response.status} failed to parse as JSON. url=%O`,
                url
            );
            responseBody = { error: responseBodyText };
        } else {
            throw jsonParseError;
        }
    }

    const hasErrorKey =
        responseBody &&
        typeof responseBody === 'object' &&
        'error' in responseBody;

    if (isHttpError && !hasErrorKey) {
        console.warn(
            `[API] Received HTTP error status ${response.status} without an error key in the response body. url=%O`,
            url,
            responseBody
        );
    } else if (!isHttpError && hasErrorKey) {
        console.warn(
            `[API] Received successful HTTP status ${response.status} but response body contains an error key. url=%O`,
            url,
            responseBody
        );
    }

    if (isHttpError || hasErrorKey) {
        const errorValue = responseBody?.error;
        const errorMessage = errorValue?.message;
        const errorMessageRepr = JSON.stringify(errorMessage) ?? 'undefined';
        console.warn(
            `[API] Error: message=${errorMessageRepr} error=%O url=%O`,
            errorValue,
            url
        );
    }

    return responseBody;
}

export interface ContextHaver {
    context: string;
}

export class ApiClient {
    constructor(
        public readonly baseURL: string,
        protected readonly auth?: FirebaseAuth
    ) {}

    public async appRequest(
        pathname: string,
        data: any,
        { context }: ContextHaver
    ): Promise<any> {
        const url = new URL(pathname, this.baseURL);
        let token = '';
        if (this.auth?.currentUser) {
            token = await getIdToken(this.auth.currentUser, false);
        }
        return performAppRequest(url, { token, data, context });
    }
}

function createApiClient() {
    const clientConfig = getAdHubClientConfig();
    const auth = getAuth();
    return new ApiClient(clientConfig.apiPrefix, auth);
}

let instance: ApiClient | null = null;
export function getApiClient(): ApiClient {
    if (!instance) {
        instance = createApiClient();
    }
    return instance;
}

/**
 * Makes a request to the AdHub API, using the current Firebase user's
 * identity token.
 *
 * @param functionName The name of the function to call
 * @param data The data to send to the function
 * @param store The store that this request is being made from
 * @returns The response from the function.
 * */
export async function functionAuthRequest(
    functionName: string,
    data: any,
    store: ContextHaver
): Promise<any> {
    // Most of the client code made requests through a module-level function
    // with this name, so I've kept that to minimize noisy diffs
    return getApiClient().appRequest(functionName, data, store);
}
